import React from 'react';
import { PRIORITY_CONFIG, CATEGORY_CONFIG } from './taskConfigs';
import { Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';

export const AnalyticsDashboard = ({ analytics, isAdmin }) => {
  if (!analytics) return <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" /></div>;

  const maxByPriority = Math.max(...Object.values(analytics.by_priority || {}), 1);
  const maxByCategory = Math.max(...Object.values(analytics.by_category || {}), 1);

  return (
    <div className="space-y-5" data-testid="analytics-dashboard">
      {/* Top Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 anim-grid-stagger">
        {[
          { label: 'Total Tasks', value: analytics.total, bg: 'text-white shadow-[0_4px_12px_rgba(217,119,6,0.30)]', style: { background: 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)' } },
          { label: 'Completed', value: analytics.completed, bg: 'bg-green-50 text-green-700 border border-green-200' },
          { label: 'Pending', value: analytics.pending, bg: 'bg-blue-50 text-blue-700 border border-blue-200' },
          { label: 'Overdue', value: analytics.overdue, bg: 'bg-red-50 text-red-700 border border-red-200' },
          { label: 'Partial', value: analytics.partial, bg: 'bg-amber-50 text-amber-700 border border-amber-200' },
          { label: 'Completion Rate', value: `${analytics.completion_rate}%`, bg: 'bg-emerald-600 text-white' },
        ].map(m => (
          <div key={m.label} className={`${m.bg} rounded-xl p-3 text-center hover:-translate-y-0.5 transition-all duration-180 ease-out`} style={m.style} data-testid={`analytics-${m.label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="text-2xl font-bold">{m.value}</div>
            <div className="text-xs opacity-80 mt-0.5">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* By Priority */}
        <div className="bg-white rounded-2xl border p-5">
          <h3 className="font-semibold text-sm text-gray-700 mb-4">Tasks by Priority</h3>
          <div className="space-y-3">
            {Object.entries(analytics.by_priority || {}).map(([key, val]) => {
              const cfg = PRIORITY_CONFIG[key] || {};
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-16">{cfg.label || key}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                    <div className={`${cfg.color || 'bg-gray-400'} h-full rounded-full transition-all`} style={{ width: `${(val / maxByPriority) * 100}%` }} />
                  </div>
                  <span className="text-xs font-bold text-gray-700 w-8 text-right">{val}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* By Category */}
        <div className="bg-white rounded-2xl border p-5">
          <h3 className="font-semibold text-sm text-gray-700 mb-4">Tasks by Category</h3>
          <div className="space-y-3">
            {Object.entries(analytics.by_category || {}).filter(([, v]) => v > 0).map(([key, val]) => {
              const cfg = CATEGORY_CONFIG[key] || {};
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-24">{cfg.icon} {cfg.label || key}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                    <div className="bg-indigo-500 h-full rounded-full transition-all" style={{ width: `${(val / maxByCategory) * 100}%` }} />
                  </div>
                  <span className="text-xs font-bold text-gray-700 w-8 text-right">{val}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Team Performance (Admin only) */}
      {isAdmin && analytics.user_stats?.length > 0 && (
        <div className="bg-white rounded-2xl border p-5">
          <h3 className="font-semibold text-sm text-gray-700 mb-4">Team Performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-gray-500">
                  <th className="text-left py-2 pr-3">Team Member</th>
                  <th className="text-center py-2 px-2">Total</th>
                  <th className="text-center py-2 px-2">Done</th>
                  <th className="text-center py-2 px-2">Pending</th>
                  <th className="text-center py-2 px-2">Overdue</th>
                  <th className="text-center py-2 px-2">Rate</th>
                  <th className="text-left py-2 pl-2 w-32">Progress</th>
                </tr>
              </thead>
              <tbody>
                {analytics.user_stats.map(u => (
                  <tr key={u.user_id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">{(u.name || '?')[0]}</div>
                        <div>
                          <div className="font-medium text-xs">{u.name}</div>
                          <div className="text-[10px] text-gray-400">{u.role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="text-center text-xs">{u.total}</td>
                    <td className="text-center text-xs text-green-600 font-medium">{u.completed}</td>
                    <td className="text-center text-xs text-blue-600">{u.pending}</td>
                    <td className="text-center text-xs text-red-600">{u.overdue}</td>
                    <td className="text-center text-xs font-bold">{u.completion_rate}%</td>
                    <td className="py-2 pl-2">
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className={`h-full rounded-full transition-all ${u.completion_rate >= 80 ? 'bg-green-500' : u.completion_rate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${u.completion_rate}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Completions */}
      {analytics.recent_completions?.length > 0 && (
        <div className="bg-white rounded-2xl border p-5">
          <h3 className="font-semibold text-sm text-gray-700 mb-3">Recent Completions (Last 7 Days)</h3>
          <div className="space-y-2">
            {analytics.recent_completions.map(t => (
              <div key={t.id} className="flex items-center gap-3 py-2 border-b last:border-0">
                {t.completion_status === 'completed' ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" /> :
                 t.completion_status === 'partial' ? <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" /> :
                 <X className="w-4 h-4 text-red-500 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-gray-700">{t.title}</span>
                  {t.completion_remarks && <span className="text-[10px] text-gray-400 ml-2">"{t.completion_remarks}"</span>}
                </div>
                <span className="text-[10px] text-gray-400 shrink-0">{t.assigned_to?.name}</span>
                <span className="text-[10px] text-gray-400 shrink-0">{t.completed_at?.slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
